/**
 * Browser push notifications — two layers:
 *   1. In-app Notification API (tab must be open)
 *   2. Web Push via Service Worker + VAPID (works even when app is closed)
 *
 * VAPID setup:
 *   1. Run: npx web-push generate-vapid-keys
 *   2. Store VAPID_PUBLIC_KEY and VAPID_PRIVATE_KEY in Amplify secrets
 *   3. Add VITE_VAPID_PUBLIC_KEY to .env.local (frontend only needs the public key)
 */

import { getDataClient } from '@/lib/amplify-client';

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function getVapidPublicKey(): Promise<string | null> {
  const fromEnv = import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined;
  if (fromEnv) return fromEnv;
  try {
    const res = await fetch('/amplify_outputs.json');
    if (res.ok) {
      const out = (await res.json()) as { custom?: { vapidPublicKey?: string } };
      return out.custom?.vapidPublicKey ?? null;
    }
  } catch { /* missing */ }
  return null;
}

export function isWebPushSupported(): boolean {
  return typeof window !== 'undefined' && 'PushManager' in window && 'serviceWorker' in navigator;
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  return navigator.serviceWorker.register('/sw.js');
}

/** Subscribe the current browser to Web Push and persist the subscription in DynamoDB. */
export async function subscribeToWebPush(userId: string): Promise<boolean> {
  if (!isWebPushSupported()) return false;

  const vapidKey = await getVapidPublicKey();
  if (!vapidKey) {
    console.warn('[ShopGND] VAPID public key not configured — push notifications disabled.');
    return false;
  }

  const reg = await registerServiceWorker();
  if (!reg) return false;

  const existing = await reg.pushManager.getSubscription();
  const sub = existing ?? (await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidKey) as unknown as ArrayBuffer,
  }));

  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!p256dh || !auth) return false;

  const client = await getDataClient();
  await client.models.PushSubscription.create({
    ownerUserId: userId,
    endpoint: sub.endpoint,
    p256dhKey: p256dh,
    authKey: auth,
    userAgent: navigator.userAgent.slice(0, 200),
  });

  return true;
}

/** Remove Web Push subscription from both browser and DynamoDB. */
export async function unsubscribeFromWebPush(userId: string): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration('/sw.js');
  if (reg) {
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
  }
  const client = await getDataClient();
  const subs = await client.models.PushSubscription.list({ filter: { ownerUserId: { eq: userId } } });
  for (const s of subs.data ?? []) await client.models.PushSubscription.delete({ id: s.id });
}

let permissionRequested = false;

export function pushNotificationsSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window;
}

export async function ensurePushPermission(): Promise<boolean> {
  if (!pushNotificationsSupported()) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;
  if (permissionRequested) return false;
  permissionRequested = true;
  const result = await Notification.requestPermission();
  return result === 'granted';
}

export function notifyNewMessage(title: string, body: string, threadId?: string) {
  if (!pushNotificationsSupported() || Notification.permission !== 'granted') return;
  if (document.visibilityState === 'visible') return;

  const n = new Notification(title, {
    body,
    tag: threadId ? `thread-${threadId}` : 'shopgnd-message',
    icon: '/shopgnd-icon-192.png',
  });
  n.onclick = () => {
    window.focus();
    if (threadId) {
      window.location.href = `/messages?thread=${encodeURIComponent(threadId)}`;
    }
    n.close();
  };
}
